import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ordersService } from '../../services';
import { supabase } from '../../lib/supabase';

// Async thunks
export const fetchArtistSales = createAsyncThunk(
  'sales/fetchArtistSales',
  async (artistId, { rejectWithValue }) => {
    try {
      const { data, error } = await ordersService.getArtistSales(artistId);
      if (error) throw new Error(error);
      
      // Debug logging for artwork data
      console.log('🎨 [SalesSlice] Raw data from Supabase:', data);
      
      // Transform the data - now we get order_items with artwork info
      const transformedData = data?.map(item => {
        // Debug specific item data
        console.log('🖼️ [SalesSlice] Order item data:', item);
        console.log('🛒 [SalesSlice] Artwork data:', item.artwork);
        
        return {
          id: item.id,
          orderId: item.order?.id,
          orderNumber: item.order?.order_number,
          artwork: {
            id: item.artwork?.id,
            title: item.artwork?.title || 'Obra sin título',
            image: item.artwork?.images?.[0] || '/placeholder-image.svg',
            price: item.price_at_time || 0
          },
          buyer: {
            name: item.order?.buyer?.full_name || 'Comprador desconocido'
          },
          purchaseDate: item.order?.created_at,
          status: item.order?.status || 'processing',
          paymentStatus: item.order?.payment_status || 'pending',
          paymentMethod: item.order?.payment_method || 'credit_card',
          mercadopagoId: item.order?.mercadopago_payment_id,
          shippedAt: item.order?.shipped_at,
          deliveredAt: item.order?.delivered_at,
          totalAmount: item.order?.total_amount || 0,
          quantity: item.quantity || 1
        };
      }) || [];

      // Separate by status into 3 categories
      const pendingShipments = transformedData.filter(sale => 
        ['pending', 'processing'].includes(sale.status.toLowerCase())
      );
      
      const inProcessSales = transformedData.filter(sale => 
        ['shipped', 'delivered'].includes(sale.status.toLowerCase())
      );
      
      const cancelledSales = transformedData.filter(sale => 
        sale.status.toLowerCase() === 'cancelled'
      );

      return { 
        pendingShipments, 
        inProcessSales, 
        cancelledSales 
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateOrderStatus = createAsyncThunk(
  'sales/updateOrderStatus',
  async ({ orderId, status }, { rejectWithValue }) => {
    try {
      // Update order status using the service (removes manual timestamp)
      const updates = {
        status,
        ...(status === 'shipped' && { shipped_at: new Date().toISOString() }),
        ...(status === 'delivered' && { delivered_at: new Date().toISOString() })
      };
      
      const { data, error } = await ordersService.updateShippingStatus(orderId, updates);
      
      if (error) throw error;
      return { orderId, status, updatedOrder: data };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  pendingShipments: [],
  inProcessSales: [],
  cancelledSales: [],
  loading: false,
  error: null
};

export const salesSlice = createSlice({
  name: 'sales',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetSales: (state) => {
      state.pendingShipments = [];
      state.inProcessSales = [];
      state.cancelledSales = [];
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch artist sales
      .addCase(fetchArtistSales.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchArtistSales.fulfilled, (state, action) => {
        state.loading = false;
        state.pendingShipments = action.payload.pendingShipments;
        state.inProcessSales = action.payload.inProcessSales;
        state.cancelledSales = action.payload.cancelledSales;
      })
      .addCase(fetchArtistSales.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update order status
      .addCase(updateOrderStatus.fulfilled, (state, action) => {
        const { orderId, status } = action.payload;
        
        if (status === 'shipped') {
          // Move from pending to in process
          const pendingIndex = state.pendingShipments.findIndex(sale => sale.orderId === orderId);
          if (pendingIndex !== -1) {
            const [movedSale] = state.pendingShipments.splice(pendingIndex, 1);
            state.inProcessSales.unshift({
              ...movedSale,
              status: 'shipped',
              shipmentDate: new Date().toISOString()
            });
          }
        }
      })
      .addCase(updateOrderStatus.rejected, (state, action) => {
        state.error = action.payload;
      });
  }
});

export const { clearError, resetSales } = salesSlice.actions;
export default salesSlice.reducer;